import type { DeepPartial } from "../../../../Common/TypingUtils";
import { CustomFile, replaceWithPdfExtension } from "../../Helpers/file";
import { CandyThunkAction } from "../Core/Store/CandyReduxTypes";
import { IForm } from "../Form/IForm";
import { CreateExplanatoryNoteResponse } from "../../../../Common/CandyApiClient/Types/ExplanatoryNote/CreateExplanatoryNoteResponse";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
} from "../../../../Common/ModelPath/ModelPath";
import { AttachmentInfoPath } from "../../../../Common/PathConstants";
import { FileProps } from "../Core/ModalManager/Modals/Utils/Types";
import { IPublicForm } from "../../Forms/PublicForm/IPublicForm";
import { AttachFormat, AttachInfo } from "../../../../Common/CandyApiClient/Types/Attachments/Attacments";
import { getCandyHub } from "../../Forms/Sentry/InitializeSentry";
import { BulkFiles } from "../Externals/Attachments/IAttachmentsManager";
import { ConvertedPDFMetaResponse } from "../../../../Common/CandyApiClient/ICandyApiClient";
import { SendChangeSetOptions } from "../Core/ChangeSet/ChangeSetManager";
import type { GosEdoPaths, GosEdoChangeablePaths } from "../../Controls/ValueEditors/FileLoader/types";
import { getIn } from "../../Helpers/ReduxStateHelpers";
import type { FullFormState } from "../Core/Store/FullFormState";

import { setToModel } from "./AllActionCreators";

export function addExplanatoryNoteAction(
    bindingPath: AbsoluteModelPath,
    noteLink: string | undefined
): CandyThunkAction<Promise<void>> {
    return async (_, __, form: IForm) => {
        if (!noteLink) {
            // eslint-disable-next-line no-console
            console.error(`Нет ссылки на анализ эксперта`);
            return;
        }

        const datetime = new Date();
        const noteName = `Финансовый анализ – ${datetime.toLocaleDateString("ru-RU")} ${datetime
            .toTimeString()
            .slice(0, 5)}.pdf`;

        const file = {
            name: noteName,
            size: 1,
            contentUri: noteLink,
        };
        await form.attachments.addFiles(bindingPath, [file]);
    };
}

export function removeAttachmentAction(
    bindingPath: AbsoluteModelPath,
    attachmentInstance?: string,
    changeSetOptions?: SendChangeSetOptions
): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        try {
            if (attachmentInstance !== undefined) {
                await form.attachments.removeFileByInstance(bindingPath, attachmentInstance, changeSetOptions);
            } else {
                await form.attachments.removeFile(bindingPath.joinWith(AttachmentInfoPath), changeSetOptions);
            }
        } catch (error) {
            getCandyHub().captureException(error);
        }
    };
}

export function clearAttachmentAction(attachmentId: string): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        const attachmentPublicForm = form.getAttachmentsPublicFormsById(attachmentId);
        await attachmentPublicForm.clearFormWithReload();
    };
}

export function removeFileAction(bindingPath: AbsoluteModelPath): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        try {
            await form.attachments.removeFile(bindingPath);
        } catch (error) {
            getCandyHub().captureException(error);
        }
    };
}

export function addAttachmentPublicFormAction(id: string, publicForm: IPublicForm): CandyThunkAction<void> {
    return (_, __, form) => {
        form.attachmentsPublicForms[id] = publicForm;
    };
}

export function openAttachmentAsDraft(gfv: string, path: AbsoluteResolvedModelPath): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.attachments.openAsDraft(gfv, path);
    };
}

export function openAttachmentFufAsDraft(attachInfo: AttachInfo): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.attachments.openFufAsDraft(attachInfo);
    };
}

export function loadAttachmentAsFile(
    loadedFileProps: FileProps,
    gfv: string,
    path: AbsoluteResolvedModelPath
): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.attachments.loadAsFile(loadedFileProps, gfv, path);
    };
}

export const getExplanatoryNoteAction: CandyThunkAction<Promise<CreateExplanatoryNoteResponse>> = (
    _,
    __,
    form: IForm
) => form.candyApiDraftClient.getExplanatoryNote();

export function addFilesAction(
    bindingPath: AbsoluteResolvedModelPath,
    files: CustomFile[]
): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.attachments.addFiles(bindingPath, files);
    };
}

export function addBulkFilesAction(bulkFiles: BulkFiles[]): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.attachments.addBulkFiles(bulkFiles);
    };
}

export function addFileAction(
    bindingPath: AbsoluteResolvedModelPath,
    file: CustomFile
): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.attachments.addFile(bindingPath, file);
    };
}

export function replaceFilesAction(
    bindingPath: AbsoluteResolvedModelPath,
    files: CustomFile[],
    replaceInstances: Iterable<string>
): CandyThunkAction<Promise<void>> {
    return async (dispatch, _, form) => {
        if (files?.length === 0) {
            return;
        }
        try {
            for (const instance of replaceInstances) {
                await form.attachments.removeFileByInstance(bindingPath, instance);
            }
            await dispatch(addFilesAction(bindingPath, files));
        } catch (error) {
            getCandyHub().captureException(error);
        }
    };
}

export function replaceFileAction(
    bindingPath: AbsoluteResolvedModelPath,
    file: CustomFile
): CandyThunkAction<Promise<void>> {
    return async (dispatch, _, form) => {
        if (!file) {
            return;
        }

        try {
            await form.attachments.removeFile(bindingPath.joinWith(AttachmentInfoPath));
            await dispatch(addFileAction(bindingPath, file));
        } catch (error) {
            getCandyHub().captureException(error);
        }
    };
}

export const downloadFileAction =
    (uri: string, fileName: string): CandyThunkAction<Promise<void>> =>
    async (_, __, form) =>
        form.candyApiDraftClient.makeDownloadDriveLink(uri, fileName);

export const convertGosEdoAttachment =
    (attachment: Required<AttachInfo>, gosEdoPaths: GosEdoPaths): CandyThunkAction<Promise<void>> =>
    async (dispatch, __, form: IForm) => {
        const meta = await form.candyApi.convertToPdfAndGetMeta(attachment.contentUri);
        dispatch(setGosEdoAttachmentMeta(meta, gosEdoPaths));

        await updateAttachmentAfterConversion(form, attachment, meta, AttachFormat.PdfA1_1_4);
    };

export const convertToPdfAction =
    (attachment: Required<AttachInfo>): CandyThunkAction<Promise<void>> =>
    async (_dispatch, __, form: IForm) => {
        const result = await form.candyApi.convertToPdf(attachment.contentUri);

        await updateAttachmentAfterConversion(form, attachment, result, AttachFormat.Pdf);
    };

const updateAttachmentAfterConversion = async (
    form: IForm,
    attachment: Required<AttachInfo>,
    meta: {
        contentUri: string;
        contentSizeBytes: number;
        isModified?: boolean;
    },
    format: AttachFormat
) => {
    const fileName = replaceWithPdfExtension(attachment.meta.fileName);
    const isConverted = meta.isModified || (attachment.meta.format != null && attachment.meta.format !== format);

    await updateAttachment(form, attachment, {
        contentUri: meta.contentUri,
        file: {
            name: fileName,
            size: meta.contentSizeBytes,
            contentUri: meta.contentUri,
        },
        meta: {
            fileName: fileName,
            fileSize: meta.contentSizeBytes,
            isConverted: isConverted,
            format: format,
        },
    });
};

const updateAttachment = async (form: IForm, attachment: Required<AttachInfo>, newData: DeepPartial<AttachInfo>) => {
    await form.attachments.updateAttachment({
        ...attachment,
        ...newData,
        file: {
            ...attachment.file,
            ...newData.file,
        },
        meta: {
            ...attachment.meta,
            ...newData.meta,
        },
    });
};

export const removeGosEdoAttachmentMeta =
    (changeableGosEdoPaths: GosEdoChangeablePaths): CandyThunkAction<void> =>
    dispatch => {
        Object.values(changeableGosEdoPaths).forEach((path: string) => {
            const thunk = setToModel(convertToAbsoluteModelPath(path), "value", null);
            dispatch(thunk);
        });
    };

const setGosEdoAttachmentMeta =
    (meta: ConvertedPDFMetaResponse, gosEdoPaths: GosEdoPaths): CandyThunkAction<void> =>
    (dispatch, getState) => {
        const { registrationTopLeftY, signatureTopLeftY } = calcCoordsFromModel(
            getState().viewModel,
            gosEdoPaths,
            meta.lastPageHeight
        );

        const dispatchMeta = (gosEdoPath: string, data: number) =>
            dispatch(setToModel(convertToAbsoluteModelPath(gosEdoPath), "value", data));
        for (const [gosEdoKey, gosEdoPath] of Object.entries(gosEdoPaths)) {
            switch (gosEdoKey) {
                case "pagesQuantity":
                case "registrationStampPage":
                case "signatureStampPage":
                    dispatchMeta(gosEdoPath, meta.pageCount);
                    break;
                case "registrationStampTopLeft":
                    dispatchMeta(gosEdoPath, registrationTopLeftY);
                    break;
                case "signatureStampTopLeft":
                    dispatchMeta(gosEdoPath, signatureTopLeftY);
                    break;
            }
        }
    };

const calcCoordsFromModel = (
    viewModel: FullFormState["viewModel"],
    gosEdoPaths: GosEdoPaths,
    lastPageHeight: number
) => {
    const signatureStampHeightKeys = gosEdoPaths.signatureStampHeight.split("/").concat(".", "value");
    const registrationStampHeightKeys = gosEdoPaths.registrationStampHeight.split("/").concat(".", "value");
    const signatureStampHeight = Number(getIn(viewModel, signatureStampHeightKeys, 0));
    const registrationStampHeight = Number(getIn(viewModel, registrationStampHeightKeys, 0));
    return calcCoordsFromLastPageHeight(lastPageHeight, signatureStampHeight, registrationStampHeight);
};

const ONE_POINT_IN_CENTIMETERS = 0.352777778;
export const calcCoordsFromLastPageHeight = (
    height: number,
    signatureStampHeight: number,
    registrationStampHeight: number
) => {
    const lastPageHeightInCentimeters = height * ONE_POINT_IN_CENTIMETERS;
    const registrationTopLeftY = Math.round(
        lastPageHeightInCentimeters - (signatureStampHeight + registrationStampHeight)
    );
    const signatureTopLeftY = Math.round(lastPageHeightInCentimeters - signatureStampHeight);
    return {
        registrationTopLeftY: Math.max(registrationTopLeftY, 0),
        signatureTopLeftY: Math.max(signatureTopLeftY, 0),
    };
};
